import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { arrayHasItems } from '../../helpers/common';
import {
  DemandPlanningLoadingKeys,
  getContainerOptimizationTableData,
} from '../../slices/demandPlanningSlice';
import { LoaderSpinner } from '../LoaderSpinner';
import { SelectDropdown } from '../SelectDropdown';
import { ContainerOptimizationSummaryBar } from './containerOptimizationComponents/ContainerOptimizationSummaryBar';
import { ContainerOptimizationInterpretationRail } from './containerOptimizationComponents/ContainerOptimizationInterpretationRail';
import { ContainerOptimizationMainTable } from './containerOptimizationComponents/ContainerOptimizationMainTable';
import { ContainerOptimizationInspectDrawer } from './containerOptimizationComponents/ContainerOptimizationInspectDrawer';
import {
  filterContainerOptimizationGroups,
  summarizeContainerOptimizationGroups,
} from '../../lib/purchasePlanningHelpers';

const GROUP_FILTER_OPTIONS = [
  { value: 'all', label: 'All pools' },
  { value: 'exceptions', label: 'Exception pools' },
  { value: 'high-fill', label: 'Fill required' },
  { value: 'healthy', label: 'Already filled' },
];

const selectDropdownClassnames = {
  selectClassname:
    '!h-9 !min-w-[180px] !max-w-[210px] !w-full !bg-transparent !text-sm !outline-none !bg-white',
  dropdownClassname: '!p-1',
  mainWrapperClassname: '!w-auto',
  itemValueClassname: '!text-sm !px-1.5 hover:!bg-primary hover:!text-white',
  selectedItemValueClassname: '!bg-primary !text-white',
};

const ContainerOptimizationTable = ({
  selectedForecastDemandPlanning,
  searchTerm,
  selectedCategoryId,
  selectedSubcategoryId,
  selectedStatusId,
  marginFromSidebar,
}) => {
  const dispatch = useDispatch();
  const { containerOptimizationTableData, loadingKeys } = useSelector(
    state => state.demandPlanning
  );

  const [localSearch, setLocalSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const containerOptimizationTableLoading = loadingKeys.includes(
    DemandPlanningLoadingKeys.getContainerOptimizationTableData
  );

  const rows = useMemo(() => {
    const raw = containerOptimizationTableData?.rows;
    return arrayHasItems(raw) ? raw : [];
  }, [containerOptimizationTableData]);

  const groups = useMemo(() => {
    const raw = containerOptimizationTableData?.groups;
    return arrayHasItems(raw) ? raw : [];
  }, [containerOptimizationTableData]);

  const supplierOptions = useMemo(() => {
    const suppliers = Array.from(new Set(groups.map(group => group.supplier).filter(Boolean)))
      .sort((left, right) => left.localeCompare(right))
      .map(supplier => ({ value: supplier, label: supplier }));
    return [{ value: 'all', label: 'All suppliers' }, ...suppliers];
  }, [groups]);

  const filteredGroups = useMemo(
    () =>
      filterContainerOptimizationGroups(groups, {
        search: localSearch,
        groupFilter,
        supplierFilter,
      }),
    [groups, localSearch, groupFilter, supplierFilter]
  );

  const selectedGroup = useMemo(
    () =>
      filteredGroups.find(group => group.id === selectedGroupId) ??
      groups.find(group => group.id === selectedGroupId) ??
      null,
    [filteredGroups, groups, selectedGroupId]
  );

  const selectedGroupRows = useMemo(() => {
    if (!selectedGroup) return [];
    if (arrayHasItems(selectedGroup.products)) return selectedGroup.products;
    return rows.filter(row => row.groupKey === selectedGroup.id);
  }, [rows, selectedGroup]);

  const summary = useMemo(
    () => summarizeContainerOptimizationGroups(filteredGroups),
    [filteredGroups]
  );

  const forecastYear =
    containerOptimizationTableData?.forecastYear ??
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear;

  useEffect(() => {
    const demandPlanningId = selectedForecastDemandPlanning?.id;
    const demandPlanningYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;

    if (demandPlanningId == null || demandPlanningYear == null || demandPlanningYear === '') return;

    setSelectedGroupId(null);

    dispatch(
      getContainerOptimizationTableData({
        demandPlanningId,
        demandPlanningYear,
        searchTerm,
        selectedCategoryId,
        selectedSubcategoryId,
        selectedStatusId,
      })
    );
  }, [
    dispatch,
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecast_year,
    selectedForecastDemandPlanning?.forecastYear,
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
  ]);

  if (containerOptimizationTableLoading) {
    return (
      <section className="flex-1 px-4 py-4 lg:px-6">
        <div className="flex justify-center items-center h-full mt-8">
          <LoaderSpinner width="80" height="80" border="5" colorSpinner="gray" />
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="mb-4 rounded-lg border border-gray-200 bg-white p-2 shadow-sm lg:p-4">
        <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <span className="mb-2 inline-flex rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-900">
              Container Optimization
            </span>
            <h2 className="text-xl font-semibold tracking-[-0.02em] text-gray-900">
              Explain how pooled supplier groups optimize container utilization before execution
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">
              Fill units here are procurement-optimization inventory, not incremental demand. The goal
              is container efficiency, freight consolidation, and traceable supplier execution.
              {forecastYear ? ` Based on OTB planning for ${forecastYear}.` : ''}
            </p>
          </div>
          <div className="flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-center xl:justify-end">
            <input
              className="h-9 w-full min-w-[220px] rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 xl:w-[260px]"
              onChange={event => setLocalSearch(event.target.value)}
              placeholder="Search supplier, group, or month"
              value={localSearch}
            />
            <SelectDropdown
              options={supplierOptions}
              value={supplierFilter}
              onChange={value => setSupplierFilter(value)}
              placeholder="All suppliers"
              portal={true}
              {...selectDropdownClassnames}
            />
            <SelectDropdown
              options={GROUP_FILTER_OPTIONS}
              value={groupFilter}
              onChange={value => setGroupFilter(value)}
              placeholder="All pools"
              portal={true}
              {...selectDropdownClassnames}
            />
          </div>
        </div>

        <ContainerOptimizationSummaryBar summary={summary} />

        <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(300px,0.82fr)_minmax(0,1.7fr)]">
          <ContainerOptimizationInterpretationRail
            filteredGroups={filteredGroups}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
          />
          <ContainerOptimizationMainTable
            groups={filteredGroups}
            selectedGroupId={selectedGroupId}
            onSelectGroup={setSelectedGroupId}
          />
        </div>
      </section>

      <ContainerOptimizationInspectDrawer
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroupId(null)}
        selectedGroup={selectedGroup}
        groupRows={selectedGroupRows}
        marginFromSidebar={marginFromSidebar}
      />
    </>
  );
};

export default ContainerOptimizationTable;
